<template>
  <div>
    <!-- Forgot Password Form -->
    <div v-if="!otpSuccess">

      <!-- Company Logo -->
      <company-logo />

      <!-- Header -->
      <list-header title="Enter Verification Code" />

      <!-- Instructions -->
      <div class="style-description text-start p-3">
        A verification code has been sent to your email address. Key in the code
        and submit your account for verification.
      </div>

      <!-- OTP Form -->
      <Form
        @submit="handleVerifyOtp"
        :validation-schema="schema"
        :initialValues="{ otp: enteredOTP }"
      >
        <div class="m-3">
          <div class="d-flex flex-row">
            <!-- OTP Field -->
            <Field name="otp" v-slot="{ handleChange, errorMessage }">
              <input
                :class="[
                  errorMessage ? 'style-error-message' : '',
                  'form-control',
                ]"
                type="number"
                placeholder="Enter Verification Code"
                v-model.trim="enteredOTP"
                @input="handleChange"
                @keydown="
                  (event) =>
                    ['e', 'E', '+', '-', '.'].includes(event.key) &&
                    event.preventDefault()
                "
              />
            </Field>

            <!-- Verify OTP Button -->
            <div class="d-flex align-items-center ms-3">
              <!-- Resend OTP Button -->
              <div v-if="timerCount === 0">
                <small-default-button-vue title="Resend" @click="sendOTP" />
              </div>
              <!-- Show timer -->
              <div v-if="timerCount > 0">
                <small-default-button-vue
                  :title="getTimerCount()"
                  :disabled="true"
                />
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div class="text-start text-danger">
            <ErrorMessage name="otp" />
          </div>
        </div>

        <div class="scss-button-position-bottom">
          <div>
            <!-- Submit OTP Button -->
            <default-button title="Submit Verification" type="submit" />
          </div>

          <div class="mb-2">
            <span
              class="scss-click-effect scss-text-blue scss-clickable"
              @click="goToLoginPage"
              >Back to login</span
            >
          </div>
        </div>
      </Form>
    </div>
    <!-- Verification Done Message -->
    <div v-if="otpSuccess">
      <div class="style-container">
        <div class="px-3 mb-5">
          <div class="style-header scss-bold">Verification Done!</div>

          <div class="mt-5">
            <img
              src="../../../assets/icons/green-tick.png"
              class="style-icon"
            />
          </div>
        </div>
      </div>

      <!-- Homepage Button -->
      <div class="scss-button-position-bottom">
        <default-button title="Sign In Now" @click="goToLoginPage" />
      </div>
    </div>
  </div>
</template>

<script>
import * as yup from "yup";
import { Form, Field, ErrorMessage } from "vee-validate";
import { validationRules } from "@/validation/ValidationRules";
import {
  mapGetters,
  mapMutations
} from "vuex";

import { requestOTP, verifyOTP, register } from "@/services/auth";
import { useToast } from "vue-toastification";

import CompanyLogo from "@/components/CompanyLogo.vue";
import ListHeader from "@/components/ListHeader.vue";
import DefaultButton from "@/components/button/DefaultButton.vue";
import SmallDefaultButtonVue from "@/components/button/SmallDefaultButton.vue";

export default {
  name: "OtpAuthentication",
  components: {
    ListHeader,
    DefaultButton,
    SmallDefaultButtonVue,
    Form,
    Field,
    ErrorMessage,
    CompanyLogo,
  },
  data() {
    return {
      timerCount: 0,
      enteredOTP: null,
      otpSuccess: false,
    };
  },
  methods: {
    goToLoginPage() {
      this.$router.push("/login");
    },
    async handleVerifyOtp() {
      this.showLoading(true);

      try {
        await verifyOTP(this.emailAddress, this.enteredOTP).then(async () => {
          const { name, phoneMobile, password, branch } =
            this.registerTemporary;
          await register(
            name,
            password,
            this.emailAddress,
            branch,
            phoneMobile
          );
        });

        // Display verified message
        this.otpSuccess = true;
        this.showLoading(false);
      } catch (error) {
        // If invalid verification code entered
        const errorMsg = error.data.code === 2007 ?
          "Invalid verification code." :
          "Oops. Something went wrong. Please try again.";
        this.showLoading(false);
        useToast().error(errorMsg);
      }
    },
    getTimerCount() {
      return this.timerCount.toString() + " secs";
    },
    async sendOTP() {
      try {
        this.showLoading(true);

        await requestOTP(this.emailAddress);
        // If success
        // Display toast message and restart counter
        this.showLoading(false);
        useToast().success("OTP Sent.");
        this.resetCounter();
      } catch (error) {
        this.showLoading(false);
        // Handle too many request
        if (error.status === 429) {
          useToast().error(error.data.message);
        }
        useToast().error("Failed to send OTP.");
        console.log(error);
      }
    },
    resetCounter() {
      this.timerCount = this.timerCount + 60;
    },
    ...mapMutations(["showLoading"]),
  },
  computed: {
    schema() {
      return yup.object({
        otp: validationRules.otp,
      });
    },
    emailAddress() {
      return Object.keys(this.duplicateCustomerTemporary).length !== 0
        ? this.duplicateCustomerTemporary.customerDetail.email
        : this.registerTemporary.email;
    },
    ...mapGetters(["registerTemporary", "duplicateCustomerTemporary"]),
  },
  watch: {
    timerCount: {
      handler(value) {
        if (value > 0) {
          setTimeout(() => {
            this.timerCount--;
          }, 1000);
        }
      },
    },
  },
  created() {
    this.sendOTP();
  },
};
</script>

<style lang="scss" scoped>
input {
  height: 56px;
  border: 1px solid #adadad;
  font-weight: 100;
}
input:focus,
select:focus {
  outline: none;
  box-shadow: none;
  border-color: #4a4a4a;
}
.style-error-message {
  border: 1px solid $error-color;
}
.style-error-message:focus {
  border-color: $error-color;
}
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
.style-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 100vh;
}

.style-header {
  color: $text-green;
  font-size: 18px;
}

.style-icon {
  width: 40%;
  height: 40%;
}
</style>
